import { useLayoutEffect, useState } from 'react';

const getMeasureAnchor = (viewportEl) =>
    viewportEl?.closest('.mtska-dashboard-container') || viewportEl?.parentElement;

/**
 * Calcola un fattore di scala (0..1] in base allo spazio disponibile del container
 * rispetto a una dimensione di riferimento (design width/height).
 * Usa un anchor esterno (dashboard container) per evitare che il viewport
 * si auto-misuri dopo essere stato espanso dal contenuto.
 */
export default function useContainerScale(containerRef, { enabled = true, designWidth = 1280, designHeight = null, minScale = 0.65, maxScale = 1 } = {}) {
    const [scale, setScale] = useState(1);

    useLayoutEffect(() => {
        if (!enabled) {
            setScale(1);
            return undefined;
        }

        const el = containerRef.current;
        if (!el) return undefined;

        const update = () => {
            const anchor = getMeasureAnchor(el);
            const availableWidth = anchor?.clientWidth > 0 ? anchor.clientWidth : window.innerWidth;
            const availableHeight = anchor?.clientHeight > 0 ? anchor.clientHeight : window.innerHeight;
            if (availableWidth <= 0) return;

            const contentEl = el.querySelector('.mtska-slider-page-wrapper') || el.querySelector('.mtska-slider');
            const contentWidth = contentEl?.scrollWidth ?? 0;
            const contentHeight = contentEl?.scrollHeight ?? 0;

            let scaleX = availableWidth / designWidth;
            if (contentWidth > availableWidth) {
                scaleX = Math.min(scaleX, availableWidth / contentWidth);
            }

            let scaleY = designHeight && availableHeight > 0 ? availableHeight / designHeight : scaleX;
            if (designHeight && contentHeight > availableHeight) {
                scaleY = Math.min(scaleY, availableHeight / contentHeight);
            }

            const next = Math.min(maxScale, scaleX, scaleY);
            setScale(Math.max(minScale, Math.min(maxScale, next)));
        };

        update();
        const ro = new ResizeObserver(update);
        const anchor = getMeasureAnchor(el);
        if (anchor) ro.observe(anchor);
        const contentEl = el.querySelector('.mtska-slider-page-wrapper') || el.querySelector('.mtska-slider');
        if (contentEl) ro.observe(contentEl);

        return () => ro.disconnect();
    }, [containerRef, enabled, designWidth, designHeight, minScale, maxScale]);

    return scale;
}
